import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { SubjectInput } from '../../planner/api';
import { COLOR_PRESETS, ICON_KEYS, ICON_LABELS, iconFor } from '../../planner/subjectStyle';

type SubjectFormProps = {
  initial?: SubjectInput;
  submitLabel: string;
  pending: boolean;
  onSubmit: (input: SubjectInput, reset: () => void) => void;
  onCancel?: () => void;
};

/** Name, icon and colour of one subject; used to add a subject and to edit one in place. */
export function SubjectForm({ initial, submitLabel, pending, onSubmit, onCancel }: SubjectFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [color, setColor] = useState(initial?.color ?? COLOR_PRESETS[0] ?? '#8c2f39');
  const [icon, setIcon] = useState(initial?.icon ?? 'book');

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), color, icon }, () => setName(''));
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-sm">
        Naam
        <Input autoFocus value={name} maxLength={40} onChange={(event) => setName(event.target.value)} className="min-h-11" />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        Icoon
        <select value={icon} onChange={(event) => setIcon(event.target.value)} className="min-h-11 rounded-lg border bg-transparent px-2">
          {ICON_KEYS.map((key) => (
            <option key={key} value={key}>
              {iconFor(key)} {ICON_LABELS[key] ?? key}
            </option>
          ))}
        </select>
      </label>
      <fieldset className="flex flex-wrap items-center gap-1">
        <legend className="sr-only">Kleur</legend>
        {COLOR_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            aria-label={`Kleur ${preset}`}
            aria-pressed={color === preset}
            onClick={() => setColor(preset)}
            className="size-9 rounded-full"
            style={{ backgroundColor: preset, outline: color === preset ? '2px solid var(--accent)' : undefined, outlineOffset: 2 }}
          />
        ))}
        <input type="color" aria-label="Eigen kleur" value={color} onChange={(event) => setColor(event.target.value)} className="size-9" />
      </fieldset>
      <div className="flex gap-2">
        <Button type="submit" disabled={pending} className="min-h-11">
          {submitLabel}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} className="min-h-11">
            Annuleren
          </Button>
        )}
      </div>
    </form>
  );
}
